import React from 'react';
import {
    styled,
    Box,
    CircularProgress,
    Typography,
    TextField,
    IconButton,
    Divider,
} from '@mui/material';
import { Card, CardHeader, CardContent } from '@mui/material';
import { TableContainer, Table, TableBody, TableRow, TableCell } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import StoreIcon from '@mui/icons-material/Store';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import { useAuth } from 'context/auth';

export default function ProductPicker(props) {
    const { title, products, bundles, error, status, onProductUpdate, onBundleUpdate } = props;

    const { auth } = useAuth();

    if (status === 'loading') {
        return (
            <Box display="flex" height="100%" alignItems="center" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    if (status === 'error' || status !== 'success') {
        return (
            <Box display="flex" height="100%" alignItems="center" justifyContent="center">
                <Typography variant="subtitle1">
                    There was a problem loading your tickets
                </Typography>
            </Box>
        );
    }

    const handleProductUpdate = (id, value) => {
        const newValue = Number(value);

        const limit = auth.admin ? 9999 : 99;

        if (Number.isInteger(newValue) && newValue >= 0 && newValue <= limit) {
            onProductUpdate(id, Number(value));
        }
    };

    const handleBundleUpdate = (id, value) => {
        const newValue = Number(value);

        const limit = auth.admin ? 9999 : 99;

        if (Number.isInteger(newValue) && newValue >= 0 && newValue <= limit) {
            onBundleUpdate(id, Number(value));
        }
    };

    const ticketsAvailable = Boolean(products.length > 0 || bundles.length > 0);

    return (
        <Card>
            <CardHeader title={title || "Build Order"} />
            <CardContent>
                {ticketsAvailable ? (
                    <>
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <DesktopProductSelect
                                products={products}
                                bundles={bundles}
                                onProductUpdate={handleProductUpdate}
                                onBundleUpdate={handleBundleUpdate}
                                error={error}
                            />
                        </Box>
                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                            <MobileProductSelect
                                products={products}
                                bundles={bundles}
                                onProductUpdate={handleProductUpdate}
                                onBundleUpdate={handleBundleUpdate}
                                error={error}
                            />
                        </Box>
                    </>
                ) : (
                    <Typography>No tickets found. Please ensure you have created at least one ticket before making an order.</Typography>
                )}
            </CardContent>
        </Card>
    );
}

function DesktopProductSelect(props) {
    const { products, bundles, onProductUpdate, onBundleUpdate, error } = props;

    return (
        <TableContainer component={Box}>
            <Table size="small">
                <TableBody>
                    {bundles.map((item) => (
                        <TableRow key={item.id}>
                            <TableCell>
                                <Box flex={2} display="flex" alignItems="center">
                                    <LocalMallIcon color="secondary" />
                                    <Box ml={1}>{item.name}</Box>
                                </Box>
                            </TableCell>
                            <TableCell width={160}>
                                <Availability icon status={item.status} />
                            </TableCell>
                            <TableCell width={120} align="right">
                                <Box display="flex" alignItems="center">
                                    <IconButton
                                        color="secondary"
                                        onClick={() => onBundleUpdate(item.id, item.quantity - 1)}
                                        size="large"
                                    >
                                        <RemoveIcon />
                                    </IconButton>
                                    <TicketField
                                        error={error}
                                        value={item.quantity}
                                        onFocus={(e) => e.target.select()}
                                        onChange={(e) => onBundleUpdate(item.id, e.target.value)}
                                    />
                                    <IconButton
                                        onClick={() => onBundleUpdate(item.id, item.quantity + 1)}
                                        size="large"
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}

                    {products.map((item) => (
                        <TableRow key={item.id}>
                            <TableCell>
                                <ProductType type={item.type} name={item.name} />
                            </TableCell>
                            <TableCell width={160}>
                                <Availability icon status={item.status} />
                            </TableCell>
                            <TableCell width={120} align="right">
                                <Box display="flex" alignItems="center">
                                    <IconButton
                                        color="secondary"
                                        onClick={() => onProductUpdate(item.id, item.quantity - 1)}
                                        size="large"
                                    >
                                        <RemoveIcon />
                                    </IconButton>
                                    <TicketField
                                        error={error}
                                        value={item.quantity}
                                        onFocus={(e) => e.target.select()}
                                        onChange={(e) => onProductUpdate(item.id, e.target.value)}
                                    />
                                    <IconButton
                                        onClick={() => onProductUpdate(item.id, item.quantity + 1)}
                                        size="large"
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function MobileProductSelect(props) {
    const { products, bundles, onProductUpdate, onBundleUpdate, error } = props;

    return (
        <>
            {bundles.map((item) => (
                <Box key={item.id}>
                    <TicketName>{item.name}</TicketName>
                    <Availability status={item.status} />

                    <Box display="flex" alignItems="center" mt={2}>
                        <IconButton
                            color="secondary"
                            onClick={() => onBundleUpdate(item.id, item.quantity - 1)}
                            size="large"
                        >
                            <RemoveIcon fontSize="large" />
                        </IconButton>
                        <TicketField
                            error={error}
                            value={item.quantity}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => onBundleUpdate(item.id, e.target.value)}
                        />
                        <IconButton
                            color="secondary"
                            onClick={() => onBundleUpdate(item.id, item.quantity + 1)}
                            size="large"
                        >
                            <AddIcon fontSize="large" />
                        </IconButton>
                    </Box>

                    <Box my={2}>
                        <Divider />
                    </Box>
                </Box>
            ))}
            {products.map((item) => (
                <Box key={item.id}>
                    <TicketName>{item.name}</TicketName>
                    <Availability status={item.status} />

                    <Box display="flex" alignItems="center" mt={2}>
                        <IconButton
                            color="secondary"
                            onClick={() => onProductUpdate(item.id, item.quantity - 1)}
                            size="large"
                        >
                            <RemoveIcon fontSize="large" />
                        </IconButton>
                        <TicketField
                            error={error}
                            value={item.quantity}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => onProductUpdate(item.id, e.target.value)}
                        />
                        <IconButton
                            color="secondary"
                            onClick={() => onProductUpdate(item.id, item.quantity + 1)}
                            size="large"
                        >
                            <AddIcon fontSize="large" />
                        </IconButton>
                    </Box>

                    <Box my={2}>
                        <Divider />
                    </Box>
                </Box>
            ))}
        </>
    );
}

function ProductType(props) {
    switch (props.type) {
        case 1:
            return (
                <Box flex={2} display="flex" alignItems="center">
                    <ConfirmationNumberIcon color="secondary" />
                    <Box ml={1}>{props.name}</Box>
                </Box>
            );
        case 2:
            return (
                <Box flex={1} display="flex" alignItems="center">
                    <AddShoppingCartIcon color="secondary" />
                    <Box ml={1}>{props.name}</Box>
                </Box>
            );
        default:
            return null;
    }
}

function Availability(props) {
    if (props.icon === true) {
        switch (props.status) {
            case 'on_sale':
                return (
                    <Box flex={1} display="flex" alignItems="center">
                        <StoreActiveIcon />
                        <Typography variant="caption">On Sale</Typography>
                    </Box>
                );
            case 'sale_not_started':
                return (
                    <Box flex={1} display="flex" alignItems="center">
                        <StoreInactiveIcon />
                        <Typography variant="caption">Sale not Started</Typography>
                    </Box>
                );
            case 'sale_concluded':
                return (
                    <Box flex={1} display="flex" alignItems="center">
                        <StoreInactiveIcon />
                        <Typography variant="caption">Sale Concluded</Typography>
                    </Box>
                );
            case 'sold_out':
                return (
                    <Box flex={1} display="flex" alignItems="center">
                        <StoreInactiveIcon />
                        <Typography variant="caption">Sold Out</Typography>
                    </Box>
                );
            case 'hidden':
                return (
                    <Box flex={1} display="flex" alignItems="center">
                        <TicketHiddenIcon />
                        <Typography variant="caption">Hidden</Typography>
                    </Box>
                );
            default:
                return <Typography>-</Typography>;
        }
    } else {
        switch (props.status) {
            case 'on_sale':
                return <Typography variant="caption">On Sale</Typography>;
            case 'sale_not_started':
                return <Typography variant="caption">Sale not Started</Typography>;
            case 'sale_concluded':
                return <Typography variant="caption">Sale Concluded</Typography>;
            case 'sold_out':
                return <Typography variant="caption">Sold Out</Typography>;
            case 'hidden':
                return <Typography variant="caption">Hidden</Typography>;
            default:
                return <Typography>-</Typography>;
        }
    }
}

const TicketField = styled(TextField)(({ theme }) => ({
    width: 56,
}));
TicketField.defaultProps = {
    variant: 'outlined',
    size: 'small',
    inputProps: {
        style: { textAlign: 'center' },
    },
};

const TicketName = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
}));

const AddIcon = styled(AddCircleOutlineIcon)(({ theme }) => ({
    color: theme.palette.success.main,
}));

const RemoveIcon = styled(RemoveCircleOutlineIcon)(({ theme }) => ({
    color: theme.palette.error.main,
}));

const StoreActiveIcon = styled(StoreIcon)(({ theme }) => ({
    color: theme.palette.success.main,
    marginRight: theme.spacing(1),
}));

const StoreInactiveIcon = styled(StoreIcon)(({ theme }) => ({
    color: theme.palette.grey[500],
    marginRight: theme.spacing(1),
}));

const TicketHiddenIcon = styled(VisibilityOffIcon)(({ theme }) => ({
    color: theme.palette.grey[500],
    marginRight: theme.spacing(1),
}));
